import { api } from '../lib/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Entity {
    entity_id: string;
    code: string;
    description: string;
    content?: string;
    alias?: string[];
    status: string;
    jd_level?: 'L1' | 'L2' | 'L3';
    created_at: string;
}

interface EntityCardProps {
    entity: Entity;
    onEdit: (entity: Entity) => void;
}

export function EntityCard({ entity, onEdit }: EntityCardProps) {
    const queryClient = useQueryClient();

    const deleteMutation = useMutation({
        mutationFn: api.deleteEntity,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['entities'] });
        },
    });

    const handleDelete = () => {
        if (confirm('Are you sure you want to delete this entity?')) {
            deleteMutation.mutate(entity.entity_id);
        }
    };

    const isHeader = entity.jd_level === 'L1';

    // Status-based styling
    const getStatusStyles = () => {
        switch (entity.status) {
            case 'ACTIVE':
                return {
                    container: '',
                    code: isHeader ? 'font-bold text-zinc-900 dark:text-zinc-100' : 'font-medium text-zinc-700 dark:text-zinc-300',
                    description: 'text-zinc-500 dark:text-zinc-500'
                };
            case 'INACTIVE':
                return {
                    container: 'opacity-60',
                    code: isHeader ? 'font-bold text-zinc-600 dark:text-zinc-400' : 'font-medium text-zinc-500 dark:text-zinc-500',
                    description: 'text-zinc-400 dark:text-zinc-600'
                };
            case 'ARCHIVED':
                return {
                    container: 'opacity-40',
                    code: isHeader ? 'font-bold text-zinc-500 dark:text-zinc-600' : 'font-medium text-zinc-400 dark:text-zinc-600',
                    description: 'text-zinc-400 dark:text-zinc-700'
                };
            default:
                return {
                    container: '',
                    code: isHeader ? 'font-bold text-zinc-900 dark:text-zinc-100' : 'font-medium text-zinc-700 dark:text-zinc-300',
                    description: 'text-zinc-500 dark:text-zinc-500'
                };
        }
    };

    const statusStyles = getStatusStyles();

    return (
        <div
            onDoubleClick={() => onEdit(entity)}
            className={`group flex items-baseline gap-3 py-1 px-2 -mx-2 hover:bg-zinc-100 dark:hover:bg-zinc-900/50 rounded transition-colors select-none cursor-pointer ${statusStyles.container}`}
        >
            <div className="min-w-0 flex-1 flex items-baseline gap-3">
                <span className={`font-mono text-sm shrink-0 ${statusStyles.code}`}>
                    {entity.code}
                </span>

                <span className={`text-xs truncate ${statusStyles.description}`}>
                    {entity.description}
                </span>
            </div>

            <div className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 flex items-center">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0 text-zinc-400 hover:text-zinc-600 dark:text-zinc-600 dark:hover:text-zinc-300"
                        >
                            <MoreHorizontal className="h-3 w-3" />
                            <span className="sr-only">Open menu</span>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-32">
                        <DropdownMenuItem onClick={() => onEdit(entity)}>
                            Edit
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                            onClick={handleDelete}
                            className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
                        >
                            Delete
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
